import type { FileUploadInput, FilePartUploadEvent } from "./types";

export function uploadFilePartCallback({
  input,
  sendBack,
  receive,
}: {
  input: {
    number: number;
    url: string;
    data: File;
  };
  sendBack: (event: FilePartUploadEvent) => void;
  receive: (callback: (event: any) => void) => void;
}) {
  const request = new XMLHttpRequest();

  request.open("PUT", input.url);

  request.upload.onprogress = (event) => {
    if (event.lengthComputable) {
      sendBack({
        type: "uploadFilePartCallback.progress",
        progress: event.loaded / event.total,
        number: input.number,
      });
    }
  };

  request.onload = (event) => {
    if (event.target) {
      if (request.status === 200) {
        const etag = (event.target as XMLHttpRequest).getResponseHeader(
          "ETag"
        ) as string;

        sendBack({
          type: "uploadFilePartCallback.complete",
          etag,
          number: input.number,
        });
      }
    }
  };

  request.onerror = (event) => {
    sendBack({ type: "uploadFilePartCallback.error", error: "Error" });
  };

  request.onabort = () => {
    sendBack({ type: "uploadFilePartCallback.abort" });
  };

  receive((event) => {
    if (event.type === "REMOVE") {
      request.abort();
    }
  });

  request.send(input.data);
}

export async function cancelUpload({
  input,
}: {
  input: {
    attachmentId: string;
  };
}) {
  return Promise.resolve();
}

export async function getUploads({
  input,
}: {
  input: { surveyId: number; orderId?: number };
}) {
  const surveyId = input.surveyId;
  const orderId = input.orderId;

  if (surveyId === undefined) {
    return [];
  }

  let url: URL;

  if (orderId) {
    // Use the new order-specific endpoint
    url = new URL(
      `/api/v1/surveys/${surveyId}/orders/${orderId}/uploads`,
      window.location.origin
    );
  } else {
    // Use the existing survey-wide endpoint
    url = new URL("/api/v1/uploads", window.location.origin);
    url.searchParams.set("surveyId", surveyId.toString());
  }

  return fetch(url).then((response) => response.json()) as Promise<
    {
      id: number;
      key: string;
      survey_id: number;
      order_id: number;
      status: "created" | "uploaded";
      file_name: string;
      file_size: number;
      file_type: string;
      file_extension: string;
      file_modified_at: number;
    }[]
  >;
}

export async function createMultipartUpload({
  input,
}: {
  input: {
    key: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    fileModifiedAt: number;
    fileExtension: string;
    surveyId: string;
    orderId: string;
  };
}) {
  return fetch(`/api/v1/uploads/multipart/create`, {
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  }).then((response) => response.json());
}

export async function presignPartUpload({
  input,
}: {
  input: {
    key: string;
    uploadId: string;
    partNumber: number;
    surveyId: number;
  };
}) {
  return fetch(`/api/v1/uploads/multipart/sign`, {
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  }).then(
    (response) =>
      response.json() as Promise<{
        presignedUrl: string;
      }>
  );
}

export async function completeMultipartUpload({
  input,
}: {
  input: {
    key: string;
    uploadId: string;
    parts: {
      PartNumber: number;
      ETag: string;
    }[];
  };
}) {
  return fetch(`/api/v1/uploads/multipart/complete`, {
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  }).then((response) => response.json());
}

export async function getUploadedParts({
  input,
}: {
  input: { key: string; uploadId: string };
}) {
  return fetch(`/api/v1/uploads/multipart/parts`, {
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  }).then((response) => response.json());
}

export async function createUploads({
  input,
}: {
  input:
    | {
        key: string;
        fileName: string;
        fileSize: number;
        fileType: string;
        fileModifiedAt: number;
        fileExtension: string;
        fileMetadata: object;
        surveyId: number;
        orderId: number;
      }
    | {
        key: string;
        fileName: string;
        fileSize: number;
        fileType: string;
        fileModifiedAt: number;
        fileExtension: string;
        fileMetadata: object;
        surveyId: number;
        orderId: number;
      }[];
}) {
  return fetch(`/api/v1/uploads`, {
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  }).then((response) => response.json());
}

export async function deleteUploads({
  input,
}: {
  input: { keys: string[]; surveyId: number; orderId: number };
}) {
  return fetch(`/api/v1/uploads`, {
    method: "delete",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  }).then((response) => response.json());
}
